import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { adminAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { AlertTriangle, Loader2, ShieldAlert, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Per-collection restore from an encrypted backup. Two-step flow:
 *  1. Preview the chosen backup (counts per collection, no DB writes).
 *  2. Pick collections + type "RESTORE" + click confirm → wipes those
 *     collections in the live DB and reinserts from the backup.
 *
 * Safety guards: typed confirmation string, blacklist on the server side,
 * and a "Restore" button that's disabled until both selections and the
 * confirm string are valid.
 */
export const BackupRestoreDialog = ({ open, onOpenChange, backup, onRestored }) => {
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (open && backup) {
      setPreview(null);
      setSelected({});
      setConfirmText('');
      setLoading(true);
      adminAPI.previewBackup(backup.id)
        .then(r => {
          setPreview(r.data);
          // Pre-select nothing — admin must opt in per collection
        })
        .catch(e => toast.error(e.response?.data?.detail || 'Could not load backup preview'))
        .finally(() => setLoading(false));
    }
  }, [open, backup]);

  const toggleAll = (checked) => {
    if (!preview) return;
    const next = {};
    if (checked) Object.keys(preview.collection_counts).forEach(c => { next[c] = true; });
    setSelected(next);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedNames = Object.keys(selected).filter(k => selected[k]);
  const canRestore = selectedCount > 0 && confirmText === 'RESTORE';

  const handleRestore = async () => {
    if (!canRestore || !backup) return;
    setRestoring(true);
    try {
      const r = await adminAPI.restoreBackup(backup.id, selectedNames);
      toast.success(`Restored ${Object.keys(r.data?.restored || {}).length} collection(s) from ${backup.filename}`);
      onOpenChange(false);
      if (onRestored) onRestored(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  if (!backup) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="backup-restore-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-600" />
            Restore from Backup
          </DialogTitle>
          <DialogDescription>
            {backup.filename} · {formatDate(backup.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2 text-sm" data-testid="restore-warning">
          <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-rose-900">
            <p className="font-semibold">This wipes the selected collections in the live database.</p>
            <p className="text-xs mt-0.5">Existing documents in chosen collections are deleted and re-created from the backup snapshot. Other collections are unaffected. Pod-runtime collections (live OAuth tokens, the backup log itself) are skipped automatically on the server.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Decrypting backup preview…
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-slate-600">
              <span>
                <strong>{preview.total_documents?.toLocaleString() || 0}</strong> documents across <strong>{Object.keys(preview.collection_counts || {}).length}</strong> collections in this backup.
              </span>
              <button
                type="button"
                onClick={() => toggleAll(selectedCount === 0)}
                className="text-blue-600 hover:text-blue-700"
                data-testid="restore-toggle-all"
              >
                {selectedCount === 0 ? 'Select all' : 'Clear selection'}
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg p-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="restore-collection-list">
              {Object.entries(preview.collection_counts || {}).sort().map(([name, count]) => (
                <label key={name} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                  <Checkbox
                    checked={!!selected[name]}
                    onCheckedChange={(v) => setSelected(prev => ({ ...prev, [name]: !!v }))}
                    data-testid={`restore-col-${name}`}
                  />
                  <span className="font-mono flex-1 truncate" title={name}>{name}</span>
                  <span className="text-slate-500 tabular-nums">{count.toLocaleString()}</span>
                </label>
              ))}
            </div>
            <div className="space-y-1">
              <Label htmlFor="restore-confirm" className="text-xs">
                Type <code className="font-mono bg-slate-100 px-1.5 rounded">RESTORE</code> to confirm
              </Label>
              <Input
                id="restore-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESTORE"
                className="font-mono"
                data-testid="restore-confirm-input"
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-8">No preview available.</p>
        )}

        <div className="flex justify-between items-center pt-2">
          <span className="text-xs text-slate-500">
            {selectedCount > 0 && `${selectedCount} collection${selectedCount === 1 ? '' : 's'} selected`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={restoring}>Cancel</Button>
            <Button
              onClick={handleRestore}
              disabled={!canRestore || restoring}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="restore-confirm-btn"
            >
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Restore {selectedCount > 0 ? `${selectedCount} ` : ''}collection{selectedCount === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BackupRestoreDialog;
